import type { ReactNode } from "react";
import type { BadgeVariant, MetricTy } from "ui/components";

import { UILoanStatus } from "./LoanStatusTag";

/** The values `LoanRow` hands to its two layouts, so the desktop table and the
 *  mobile cards can never disagree about what a loan says. Not a public API —
 *  `LoanRow` derives this from a `Loan` itself. */
export type LoanRowData = {
  /** Collateral name from the loan's metadata, or `"-"` when it has none. */
  collateral: string;
  status: UILoanStatus;
  /** Principal node — an `AmountTooltip`, matching the old loans table. */
  principal: ReactNode;
  /** Formatted APR, e.g. "15.05%". */
  apr: string;
  /** Next payment date, e.g. "30 May 2026". */
  dueDate: string;
  /** Relative line under the date, e.g. "2d 3h overdue" / "Due today". */
  dueNote: string;
  /** Payment amount, quote-derived: a node, `null` for `--`, `undefined` for a
   *  skeleton. See {@link MetricTy}. */
  amount: MetricTy;
};

/**
 * Badge variant per status, from the Figma frames.
 *
 * Partial on purpose: `getBorrowerLoans` returns only `Active` loans, so
 * `Pending`, `Closed`, `AtAuction` and `Auctioned` cannot reach this table and
 * no frame assigns them a badge here. The Inactive Loans frame draws its own
 * "Defaulted" / "Paid Off" badges, which do not map 1:1 onto these members —
 * so guessing values for them now would most likely be wrong. Add entries when
 * that section is actually built.
 */
export const STATUS_BADGE: Partial<Record<UILoanStatus, BadgeVariant>> = {
  [UILoanStatus.Late]: "destructive",
  [UILoanStatus.PayNow]: "warning",
  [UILoanStatus.Current]: "positive",
};

export const FALLBACK_STATUS_BADGE: BadgeVariant = "tertiary";

/** Color of the due note. Only the two urgent statuses tint it; the rest keep
 *  the neutral tone, which the frames set differently per breakpoint. */
export const STATUS_NOTE_COLOR: Partial<Record<UILoanStatus, string>> = {
  [UILoanStatus.Late]: "text-sys-negative-negative",
  [UILoanStatus.PayNow]: "text-sys-warning-warning",
};
